import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useTranslation } from 'react-i18next';

import Text, { TEXT_COLOR, TEXT_SIZE, TEXT_WEIGHT } from '@components/Text';
import TextArea from '@components/TextArea';
import { Tooltip } from '@components/Tooltip';
import { STIMULUS_WORDS_COUNT } from '@shared/Resources/Monitor/types';
import { POPULATION_OPTIONS } from '../tabs/SpeechTopics/config';
import { LOCALE_OPTIONS } from './locale';
import { formatDateTime, formatPopulation, formatSentenceWithSyllables, formatWordsWithSyllables } from './formatters';
import { arrayShallowEqual } from './collections';
import {
  DetailsActionButton,
  DetailsActions,
  DetailsActionsButtons,
  DetailsEditToolbar,
  DetailsError,
  DetailsField,
  DetailsHeaderRow,
  DetailsHint,
  DetailsInlineRow,
  DetailsPrimaryButton,
  DetailsSelect,
} from './detailsEditorStyles';

const INTONATION_OPTIONS = [
  { label: 'FACT', value: 'FACT' },
  { label: 'QUESTION', value: 'QUESTION' },
  { label: 'FACT_AND_QUESTION', value: 'FACT_AND_QUESTION' },
];

const SENTENCE_LENGTH_OPTIONS = [
  { label: 'Not marked (default by syllables)', value: '' },
  { label: STIMULUS_WORDS_COUNT.SHORT, value: STIMULUS_WORDS_COUNT.SHORT },
  { label: STIMULUS_WORDS_COUNT.MEDIUM, value: STIMULUS_WORDS_COUNT.MEDIUM },
  { label: STIMULUS_WORDS_COUNT.LONG, value: STIMULUS_WORDS_COUNT.LONG },
];

const normalizeForm = (item) => ({
  locale: item?.locale || LOCALE_OPTIONS[0]?.value || 'EN',
  text: Array.isArray(item?.words) && item.words.length
    ? formatSentenceWithSyllables(item.words, { originalText: item?.text })
    : (item?.text || ''),
  intonation: item?.intonation || INTONATION_OPTIONS[0]?.value || 'FACT',
  sentenceLength: item?.sentenceLength || '',
  population: Array.isArray(item?.population) && item.population.length
    ? item.population
    : [POPULATION_OPTIONS[0]?.value].filter(Boolean),
});

const StimulusSentenceDetailsEditor = ({ item, onSave, saving }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => normalizeForm(item));

  useEffect(() => {
    if (!isEditing) {
      setForm(normalizeForm(item));
    }
  }, [item, isEditing]);

  const viewPopulationLabel = useMemo(
    () => formatPopulation(item?.population, POPULATION_OPTIONS),
    [item?.population]
  );

  const hasChanges = useMemo(() => {
    const base = normalizeForm(item);
    return base.locale !== form.locale
      || base.text !== form.text
      || base.intonation !== form.intonation
      || base.sentenceLength !== form.sentenceLength
      || !arrayShallowEqual(base.population, form.population);
  }, [form, item]);

  const validationMessage = useMemo(() => {
    if (!form.text.trim()) return t('Stimuli.textRequired');
    if (!form.population.length) return t('Stimuli.populationRequired');
    return '';
  }, [form.population.length, form.text, t]);

  const saveTooltipMessage = useMemo(() => {
    if (saving) return '';
    if (validationMessage) return '';
    if (!hasChanges) return t('Stimuli.saveAfterChange');
    return '';
  }, [hasChanges, saving, t, validationMessage]);

  const isSaveDisabled = saving || !hasChanges || !form.text.trim() || !form.population.length;
  const detailsDir = form.locale === 'HE' ? 'rtl' : 'ltr';

  const togglePopulation = (value) => {
    setForm((prev) => {
      const has = prev.population.includes(value);
      if (has) {
        return { ...prev, population: prev.population.filter((p) => p !== value) };
      }
      return { ...prev, population: [...prev.population, value] };
    });
  };

  const handleCancel = () => {
    setForm(normalizeForm(item));
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    const saveSucceeded = await onSave({
      locale: form.locale,
      text: form.text,
      intonation: form.intonation,
      sentenceLength: form.sentenceLength || null,
      population: form.population,
    });
    if (saveSucceeded) {
      setIsEditing(false);
    }
  };

  const syllablePreviewLines = useMemo(() => {
    const words = item?.words || [];
    return formatWordsWithSyllables(words, { originalText: item?.text });
  }, [item?.text, item?.words]);

  return (
    <>
      {!isEditing ? (
        <>
          <DetailsHeaderRow>
            <Text size={TEXT_SIZE.H3} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.sentenceDetails')}
            </Text>
            <DetailsEditToolbar>
              <DetailsActionButton type="button" onClick={() => setIsEditing(true)} title={t('Stimuli.editSentence')}>
                <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {t('Actions.Instance.edit')}
              </DetailsActionButton>
            </DetailsEditToolbar>
          </DetailsHeaderRow>

          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.locale')}: {item?.locale || ''}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.intonation')}: {item?.intonation || ''}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.sentenceLength')}: {item?.sentenceLength || t('Stimuli.notMarked')}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.population')}: {viewPopulationLabel}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.wordCount')}: {typeof item?.wordCount === 'number' ? item.wordCount : ''}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.syllableCount')}: {typeof item?.syllableCount === 'number' ? item.syllableCount : ''}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            Created At: {formatDateTime(item?.createdAt)}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            Updated At: {formatDateTime(item?.updatedAt)}
          </Text>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.fullText')}
            </Text>
            <TextArea value={item?.text || ''} readOnly dir={item?.locale === 'HE' ? 'rtl' : 'ltr'} style={{ height: 120, width: '100%' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.dividedWordsSyllables')}
            </Text>
            <TextArea
              value={syllablePreviewLines.join('\n')}
              readOnly
              dir={detailsDir}
              style={{ height: 240, width: '100%' }}
            />
          </div>
        </>
      ) : (
        <>
          <Text size={TEXT_SIZE.H3} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
            {t('Stimuli.sentenceDetails')}
          </Text>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.locale')}
            </Text>
            <DetailsSelect
              value={form.locale}
              onChange={(e) => setForm((prev) => ({ ...prev, locale: e.target.value }))}
              disabled={saving}
            >
              {LOCALE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </DetailsSelect>
          </DetailsField>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.intonation')}
            </Text>
            <DetailsSelect
              value={form.intonation}
              onChange={(e) => setForm((prev) => ({ ...prev, intonation: e.target.value }))}
              disabled={saving}
            >
              {INTONATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </DetailsSelect>
          </DetailsField>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.sentenceLength')}
            </Text>
            <DetailsSelect
              value={form.sentenceLength}
              onChange={(e) => setForm((prev) => ({ ...prev, sentenceLength: e.target.value }))}
              disabled={saving}
            >
              {SENTENCE_LENGTH_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </DetailsSelect>
          </DetailsField>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.population')}
            </Text>
            <DetailsInlineRow>
              {POPULATION_OPTIONS.map((opt) => {
                const checked = form.population.includes(opt.value);
                return (
                  <label key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      onChange={() => togglePopulation(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
              {!form.population.length ? <DetailsError>{t('Stimuli.populationRequired')}</DetailsError> : null}
            </DetailsInlineRow>
          </DetailsField>

          <DetailsField>
            <DetailsInlineRow>
              <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
                {t('Stimuli.fullText')}
              </Text>
              {!form.text.trim() ? (
                <DetailsError>{t('Stimuli.textRequired')}</DetailsError>
              ) : (
                <DetailsHint>{t('Stimuli.sentenceTextFormatHint')}</DetailsHint>
              )}
            </DetailsInlineRow>
            <TextArea
              value={form.text}
              onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
              dir={detailsDir}
              style={{ height: 120, width: '100%' }}
            />
            <DetailsHint>{t('Stimuli.sentenceCountsUpdateAfterSave')}</DetailsHint>
          </DetailsField>

          <DetailsActions>
            <div>
              {validationMessage ? <DetailsError>{validationMessage}</DetailsError> : null}
            </div>
            <DetailsActionsButtons>
              <DetailsActionButton type="button" onClick={handleCancel} disabled={saving}>
                <CloseOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {t('Actions.Instance.cancel')}
              </DetailsActionButton>
              {saveTooltipMessage ? (
                <Tooltip content={saveTooltipMessage} placement="top" hasArrow>
                  <span>
                    <DetailsPrimaryButton
                      type="button"
                      onClick={handleSave}
                      disabled={isSaveDisabled}
                    >
                      <SaveOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {saving ? t('Stimuli.saving') : t('Actions.Instance.save')}
                    </DetailsPrimaryButton>
                  </span>
                </Tooltip>
              ) : (
                <DetailsPrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  <SaveOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {saving ? t('Stimuli.saving') : t('Actions.Instance.save')}
                </DetailsPrimaryButton>
              )}
            </DetailsActionsButtons>
          </DetailsActions>
        </>
      )}
    </>
  );
};

StimulusSentenceDetailsEditor.propTypes = {
  item: PropTypes.shape({
    locale: PropTypes.string,
    text: PropTypes.string,
    intonation: PropTypes.string,
    sentenceLength: PropTypes.string,
    population: PropTypes.arrayOf(PropTypes.string),
    wordCount: PropTypes.number,
    syllableCount: PropTypes.number,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    words: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      syllableCount: PropTypes.number,
      syllables: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
    })),
  }),
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

StimulusSentenceDetailsEditor.defaultProps = {
  item: null,
  saving: false,
};

export default StimulusSentenceDetailsEditor;
